import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { ImageDown, Loader2, Calendar, Building2 } from "lucide-react";

const ALL_DISTRICTS = "__all__";
const ALL_YEARS = "__all__";

function fmtBytes(n) {
    if (!n) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    let i = 0;
    let v = n;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

export default function PhotoDownloadDialog({ open, onOpenChange, initialDistrictId = "", initialYear = "" }) {
    const [districts, setDistricts] = useState([]);
    const [districtId, setDistrictId] = useState(initialDistrictId || ALL_DISTRICTS);
    const [year, setYear] = useState(initialYear || ALL_YEARS);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [includeSpouse, setIncludeSpouse] = useState(true);
    const [preview, setPreview] = useState(null); // { count, total_bytes }
    const [previewing, setPreviewing] = useState(false);
    const [downloading, setDownloading] = useState(false);

    // If parent supplied initial selection, snap to it every time the dialog reopens.
    useEffect(() => {
        if (!open) return;
        if (initialDistrictId) setDistrictId(initialDistrictId);
        if (initialYear) setYear(initialYear);
    }, [open, initialDistrictId, initialYear]);

    // Load districts when dialog opens
    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const { data } = await api.get("/super/districts");
                setDistricts(data);
            } catch (_) {
                toast.error("Could not load districts");
            }
        })();
    }, [open]);

    const availableYears = useMemo(() => {
        if (districtId === ALL_DISTRICTS) return [];
        const d = districts.find((x) => x.id === districtId);
        return d?.years || [];
    }, [districtId, districts]);

    // Build query params
    const params = useMemo(() => {
        const q = new URLSearchParams();
        if (districtId !== ALL_DISTRICTS) q.set("district_id", districtId);
        if (year !== ALL_YEARS) q.set("year", year);
        if (dateFrom) q.set("date_from", dateFrom);
        if (dateTo) q.set("date_to", dateTo);
        q.set("include_spouse", includeSpouse ? "true" : "false");
        return q.toString();
    }, [districtId, year, dateFrom, dateTo, includeSpouse]);

    // Debounced preview
    useEffect(() => {
        if (!open) return;
        let alive = true;
        setPreviewing(true);
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get(`/admin/photos/preview?${params}`);
                if (alive) setPreview(data);
            } catch (_) {
                if (alive) setPreview({ count: 0, total_bytes: 0 });
            } finally {
                if (alive) setPreviewing(false);
            }
        }, 250);
        return () => { alive = false; clearTimeout(t); };
    }, [params, open]);

    const reset = () => {
        setDistrictId(ALL_DISTRICTS);
        setYear(ALL_YEARS);
        setDateFrom("");
        setDateTo("");
        setIncludeSpouse(true);
    };

    const download = async () => {
        setDownloading(true);
        try {
            const res = await api.get(`/admin/photos/download-all?${params}`, {
                responseType: "blob",
            });
            const count = res.headers?.["x-photo-count"] || preview?.count || "";
            const blob = new Blob([res.data], { type: "application/zip" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const stamp = new Date().toISOString().slice(0, 10);
            a.download = `member-photos-${stamp}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(count ? `Downloaded ${count} photos` : "Photos downloaded");
            onOpenChange(false);
        } catch (_) {
            toast.error("Photo download failed");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg" data-testid="photo-download-dialog">
                <DialogHeader>
                    <DialogTitle className="rotary-heading text-2xl flex items-center gap-2">
                        <ImageDown className="h-5 w-5 text-rotary-blue" />
                        Download Photos
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground">
                        Choose a district, year, or date range to filter which photos are zipped.
                    </p>
                </DialogHeader>

                <div className="space-y-4">
                    {/* District + Year */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="label-eyebrow flex items-center gap-1.5">
                                <Building2 className="h-3 w-3" /> District
                            </Label>
                            <Select
                                value={districtId}
                                onValueChange={(v) => { setDistrictId(v); setYear(ALL_YEARS); }}
                            >
                                <SelectTrigger data-testid="photo-district-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ALL_DISTRICTS}>All districts</SelectItem>
                                    {districts.map((d) => (
                                        <SelectItem key={d.id} value={d.id}>
                                            RID {d.district_number}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="label-eyebrow flex items-center gap-1.5">
                                <Calendar className="h-3 w-3" /> Rotary year
                            </Label>
                            <Select
                                value={year}
                                onValueChange={setYear}
                                disabled={districtId === ALL_DISTRICTS}
                            >
                                <SelectTrigger data-testid="photo-year-select">
                                    <SelectValue placeholder={districtId === ALL_DISTRICTS ? "Pick district first" : "All years"} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ALL_YEARS}>All years</SelectItem>
                                    {availableYears.map((y) => (
                                        <SelectItem key={y.id} value={y.year}>{y.year}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Date range */}
                    <div>
                        <Label className="label-eyebrow">Uploaded between</Label>
                        <div className="mt-1 grid grid-cols-2 gap-3">
                            <Input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                data-testid="photo-date-from"
                                aria-label="From date"
                            />
                            <Input
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                data-testid="photo-date-to"
                                aria-label="To date"
                            />
                        </div>
                        <p className="mt-1 text-[11px] text-muted-foreground">
                            Leave blank for no date limit. Uses each file&apos;s last-modified time on the server.
                        </p>
                    </div>

                    {/* Include spouse */}
                    <label className="flex items-center gap-2 rounded-lg border p-3">
                        <input
                            type="checkbox"
                            checked={includeSpouse}
                            onChange={(e) => setIncludeSpouse(e.target.checked)}
                            className="h-4 w-4"
                            data-testid="photo-include-spouse"
                        />
                        <div>
                            <p className="text-sm font-medium">Include spouse photos</p>
                            <p className="text-[11px] text-muted-foreground">Files ending in <code>_s</code> before extension.</p>
                        </div>
                    </label>

                    {/* Preview */}
                    <div
                        className="rounded-lg border bg-muted/30 px-4 py-3 flex items-center justify-between"
                        data-testid="photo-preview-summary"
                    >
                        <div>
                            <p className="rotary-heading text-2xl leading-none text-rotary-ink">
                                {previewing ? (
                                    <Loader2 className="inline h-5 w-5 animate-spin text-rotary-blue" />
                                ) : (
                                    (preview?.count ?? 0).toLocaleString()
                                )}
                            </p>
                            <p className="mt-1 text-[11px] uppercase tracking-widest text-muted-foreground">
                                photos matched
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-sm font-medium">{fmtBytes(preview?.total_bytes || 0)}</p>
                            <p className="text-[11px] text-muted-foreground">estimated size</p>
                        </div>
                    </div>
                </div>

                <DialogFooter className="flex justify-between sm:justify-between">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={reset}
                        className="rounded-full"
                        data-testid="photo-reset-btn"
                    >
                        Reset filters
                    </Button>
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            className="rounded-full"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={download}
                            disabled={downloading || previewing || !preview?.count}
                            className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky"
                            data-testid="photo-download-confirm-btn"
                        >
                            {downloading ? (
                                <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Zipping…</>
                            ) : (
                                <><ImageDown className="mr-1.5 h-4 w-4" /> Download {preview?.count || 0}</>
                            )}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
